import { EMAIL, IN_APP, WHATS_APP } from 'constants/messageMediums';
import { selectIsWhatsAppInfobipLocalesEnabled } from 'core/userContext/selectors';
import { ANDROID, IN_APP_VIEW } from 'modules/templatePreview/constants';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'ui/skeleton';
import { WhatsAppStatusBadges } from './badges/WhatsAppStatusBadges';

import {
  getInAppContent,
  previewTemplate,
  setActivePushDeviceWrapperState,
} from 'modules/templatePreview/actions';
import {
  getActivePushDeviceWrapperById,
  getInAppDisplaySettingsById,
  getInboxMetadataById,
  getIsLoading,
  getTemplatePreviewById,
} from 'modules/templatePreview/selectors';

import { ActionBar } from './ActionBar';
import { InAppIframe } from './InAppIframe';
import { IframeContainer, PreviewContainer } from './TemplatePreview.styled';
import { renderIframe } from './utils';

// type Props = {
//   locale?: any,
//   messageMedium?: MEDIUMS,
//   showInAppToggleButtons?: boolean,
//   showPushToggleButtons?: boolean,
//   size?: CARD | THUMBNAIL,
//   templateId: number,
// };
/**
 * TemplatePreview
 * @param { any } locale  the locale of the template being displayed
 * @param { string } messageMedium  the template message medium
 * @param { boolean } showInAppToggleButtons  whether to show the in-app app/inbox toggle
 * @param { boolean } showPushToggleButtons whether to show the push apple/android toggle
 * @param { string } size  the size of the preview
 * @param { number } templateId  the id of the template to preview
 */
export const TemplatePreview = ({
  /** the locale of the template being displayed */
  locale = undefined,
  /** the template message medium */
  messageMedium = EMAIL,
  /** whether to show the in-app app/inbox toggle */
  showInAppToggleButtons = false,
  /** whether to show the push apple/android toggle */
  showPushToggleButtons = false,
  size = 'card',
  templateId,
  whatsAppLocaleContent = [],
}) => {
  const dispatch = useDispatch();

  // --- PUSH message medium specific settings ---
  const activePushDeviceWrapper = useSelector((state) =>
    getActivePushDeviceWrapperById(state, { templateId }),
  );
  // the state for which push wrapper is currently visible is kept in redux,
  // because the preview can be refreshed from outside of this component.
  // see the workflow TemplatePreview component for such a case.
  // consequently we need to know which preview to refresh outside of this local component.
  const setPushDevicePreviewWrapper = useCallback(
    (pushDeviceWrapper) => dispatch(setActivePushDeviceWrapperState(templateId, pushDeviceWrapper)),
    [templateId],
  );

  // --- IN-APP message medium specific settings ---
  const isInAppMessage = messageMedium === IN_APP;

  // in-app messages can have an inbox.
  // the preview needs to be able to toggle between showing the template and
  // the inbox view of that message
  const [inAppView, setInAppView] = useState(IN_APP_VIEW);
  const inboxMetadata = useSelector((state) => getInboxMetadataById(state, { templateId }));

  // we should only show the inbox toggle if the message actually has inbox content
  // which we can tell is true if the inboxdata has either a set title field or a set subtitle field.
  // (these cannot be empty strings)
  const showInboxToggle =
    !!(inboxMetadata?.title || inboxMetadata?.subtitle) && showInAppToggleButtons;

  // in-app messages can also have:
  // 1. a background layer with a custom hex background value,
  // 2. with a custom opacity on that layer,
  // 3. and the message can be displayed either at the top, center, bottom,
  //    or across the full view of the app screen.
  // All of that info is stored in the "inAppDisplaySettings"
  const inAppDisplaySettings = useSelector((state) =>
    getInAppDisplaySettingsById(state, { templateId }),
  );

  useEffect(() => {
    // we only need to load the data from the backend if inAppDisplaySettings aren't set.
    // the inbox is optional, so checking against it doesn't tell us if the data exist, whereas
    // the inAppDisplaySettings will always be populated with some data.
    if (!!inAppDisplaySettings) {
      return;
    }

    if (messageMedium === IN_APP) {
      // "getInAppContent" will get us both the inbox and inAppDisplay data
      dispatch(getInAppContent(templateId));
    }
  }, [inAppDisplaySettings]);
  // --- IN-APP specific settings ---

  // WhatsApp
  const isWhatsAppGAEnabled = useSelector(selectIsWhatsAppInfobipLocalesEnabled);

  const isLoading = useSelector(getIsLoading);
  const previewHtml = useSelector((state) =>
    getTemplatePreviewById(state, {
      templateId,
      locale,
      showAndroid: activePushDeviceWrapper === ANDROID,
    }),
  );

  useEffect(() => {
    // don't run if we already stored the preview html.
    if (previewHtml) {
      return;
    }

    // i only want this to run once, on the first load.
    // if we don't query the first time...a retry won't help,
    // because something is borked with the html of the template
    dispatch(
      previewTemplate({
        templateId,
        messageMedium,
        showAndroid: activePushDeviceWrapper === ANDROID, // for push messages,
        showExpanded: false, // for push messages
        showLighthouseVersion: true, // for in-app, sms, push messages
        locale,
      }),
    );
  }, [locale, activePushDeviceWrapper]);

  if (isLoading) {
    return (
      <PreviewContainer data-test="template-preview">
        <Skeleton />
      </PreviewContainer>
    );
  }

  const Iframe = renderIframe(messageMedium);

  return (
    <>
      <ActionBar
        selectedPushToggleButton={activePushDeviceWrapper}
        messageMedium={messageMedium}
        onToggle={isInAppMessage ? setInAppView : setPushDevicePreviewWrapper}
        showInAppToggleButtons={showInboxToggle}
        showPushToggleButtons={showPushToggleButtons}
      />
      <PreviewContainer data-test="template-preview" size={size}>
        {isInAppMessage ? (
          <InAppIframe
            inAppDisplaySettings={inAppDisplaySettings}
            inAppView={inAppView}
            inboxMetadata={inboxMetadata}
            previewHtml={previewHtml}
            size={size}
          />
        ) : (
          <>
            {messageMedium === WHATS_APP &&
              isWhatsAppGAEnabled &&
              !!whatsAppLocaleContent?.length && (
                <WhatsAppStatusBadges whatsAppLocaleContent={whatsAppLocaleContent} />
              )}
            <IframeContainer>
              <Iframe
                tabIndex="-1" // don't want content inside the iframe to be focusable
                data-test={`template-preview-${messageMedium}-iframe`}
                scrolling="no"
                srcDoc={previewHtml}
                sandbox="" // disables js, etc. inside of the iframe
              />
            </IframeContainer>
          </>
        )}
      </PreviewContainer>
    </>
  );
};
